import { useEffect, useState } from "react";
import { useSettings } from "@/context/SettingsContext";
import { Plus, Edit2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import api from "@/utils/api";

const MembershipPlans = () => {
  const { formatDate, formatCurrency, settings } = useSettings();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    duration_days: "",
    price: "",
    features: "",
    status: "active",
  });

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await api.get('/membership-plans');
      setPlans(response.data);
    } catch (error) {
      console.error("Failed to fetch plans:", error);
      toast.error("Failed to load membership plans");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const featuresArray = formData.features.split('\n').filter(f => f.trim());
      const payload = {
        name: formData.name,
        duration_days: parseInt(formData.duration_days),
        price: parseFloat(formData.price),
        features: featuresArray,
        status: formData.status,
      };

      if (editingPlan) {
        await api.patch(`/membership-plans/${editingPlan.id}`, payload);
        toast.success("Plan updated successfully");
      } else {
        await api.post('/membership-plans', payload);
        toast.success("Plan added successfully");
      }
      fetchPlans();
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error("Error saving plan:", error);
      toast.error(error.response?.data?.detail || "Failed to save plan");
    }
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      duration_days: plan.duration_days.toString(),
      price: plan.price.toString(),
      features: plan.features.join('\n'),
      status: plan.status || "active",
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this plan?")) {
      try {
        await api.delete(`/membership-plans/${id}`);
        toast.success("Plan deleted successfully");
        fetchPlans();
      } catch (error) {
        console.error("Error deleting plan:", error);
        toast.error("Failed to delete plan");
      }
    }
  };

  const handleToggleStatus = async (plan) => {
    try {
      const newStatus = plan.status === "active" ? "inactive" : "active";
      await api.patch(`/membership-plans/${plan.id}`, { status: newStatus });
      
      setPlans(plans.map(p => 
        p.id === plan.id ? { ...p, status: newStatus } : p
      ));
      
      toast.success(`Plan marked as ${newStatus}`);
    } catch (error) {
      console.error("Error updating plan status:", error);
      toast.error("Failed to update plan status");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      duration_days: "",
      price: "",
      features: "",
      status: "active",
    });
    setEditingPlan(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lime-400"></div>
      </div>
    );
  }

  return (
    <div data-testid="plans-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-4xl font-black tracking-tight mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Membership Plans
          </h1>
          <p className="text-zinc-500">Manage subscription plans</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) resetForm();
          }}>
          <DialogTrigger asChild>
            <Button 
              data-testid="add-plan-btn"
              className="bg-lime-400 text-zinc-900 hover:bg-lime-500 font-bold shadow-sm transition-all active:scale-95"
            >
              <Plus size={20} className="mr-2" />
              Add Plan
            </Button>
          </DialogTrigger>
          <DialogContent aria-describedby="plan-dialog-description">
            <DialogHeader>
              <DialogTitle>{editingPlan ? "Edit Plan" : "Add New Plan"}</DialogTitle>
            </DialogHeader>
            <p id="plan-dialog-description" className="sr-only">
              {editingPlan ? "Edit membership plan details" : "Create a new membership plan"}
            </p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Plan Name</Label>
                <Input
                  id="name"
                  data-testid="plan-name-input"
                  placeholder="e.g., Monthly, Quarterly, Annual"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="duration_days">Duration (Days)</Label>
                <Input
                  id="duration_days"
                  type="number"
                  data-testid="plan-duration-input"
                  placeholder="30, 90, 365"
                  value={formData.duration_days}
                  onChange={(e) => setFormData({ ...formData, duration_days: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="price">Price ($)</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  data-testid="plan-price-input"
                  placeholder="49.99"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="features">Features (one per line)</Label>
                <Textarea
                  id="features"
                  data-testid="plan-features-input"
                  placeholder="Unlimited gym access\nPersonal trainer sessions\nFree parking"
                  value={formData.features}
                  onChange={(e) => setFormData({ ...formData, features: e.target.value })}
                  rows={4}
                  required
                />
              </div>
              <Button 
                type="submit" 
                data-testid="plan-submit-btn"
                className="w-full bg-lime-400 text-zinc-900 hover:bg-lime-500 font-bold"
              >
                {editingPlan ? "Update" : "Add"} Plan
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {plans.length === 0 ? (
          <div className="col-span-full text-center py-12 text-zinc-500">
            No plans found. Add your first membership plan to get started.
          </div>
        ) : (
          plans.map((plan) => (
            <div
              key={plan.id}
              data-testid={`plan-card-${plan.id}`}
              className={`bg-white border border-zinc-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-all ${
                plan.status === 'inactive' ? 'opacity-75 bg-zinc-50' : ''
              }`}
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-xl font-bold text-zinc-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
                      {plan.name}
                    </h3>
                    {plan.status === 'inactive' && (
                      <span className="px-2 py-0.5 rounded-full bg-zinc-200 text-zinc-600 text-xs font-medium">
                        Inactive
                      </span>
                    )}
                  </div>
                  <div className="text-3xl font-black text-zinc-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    {formatCurrency(plan.price)}
                  </div>
                  <p className="text-zinc-500 text-sm">
                    {plan.duration_days} days
                  </p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <div className="flex items-center gap-2" title="Toggle Active Status">
                    <span className={`text-xs font-medium ${plan.status === 'active' ? 'text-lime-600' : 'text-zinc-500'}`}>
                      {plan.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                    <Switch
                      checked={plan.status === 'active'}
                      onCheckedChange={() => handleToggleStatus(plan)}
                    />
                  </div>
                  <div className="flex gap-1 mt-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(plan)}
                      className="text-zinc-400 hover:text-zinc-900"
                    >
                      <Edit2 size={18} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(plan.id)}
                      className="text-zinc-400 hover:text-red-600"
                    >
                      <Trash2 size={18} />
                    </Button>
                  </div>
                </div>
              </div>

              <div className="border-t border-zinc-200 pt-4">
                <p className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Features</p>
                <ul className="space-y-1.5">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm text-zinc-600">
                      <span className="text-lime-500 mt-0.5">✓</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default MembershipPlans;